import React from "react";
import { Box, Typography, Button, Tooltip, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";

// Styled Box for the Recipe Card with scoped styles
const RecipeCardContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#f5deb3", // Parchment color
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    maxWidth: "800px",
    margin: "0 auto",
    position: "relative",
    fontFamily: "'Times New Roman', serif", // Isolated font style
    color: "black", // Override any theme font color
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: "absolute",
    top: "10px",
    right: "10px",
    color: "red",
}));

const USDARecipeCard = ({ recipe, onClose }) => {
    const handlePrint = () => {
        const printContent = document.getElementById("recipe-card");
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Recipe</title>
                    <style>
                        body { margin: 0; padding: 20px; font-family: 'Times New Roman', serif; }
                        #recipe-card { max-width: 100%; box-sizing: border-box; }
                    </style>
                </head>
                <body>
                    ${printContent.outerHTML}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    const handleCopy = () => {
        const recipeText = `
Recipe: ${recipe.name || "Recipe Title"}
Servings: ${recipe.servings || "50"}
Time: ${recipe.time || "45 minutes"}

Ingredients:
${recipe.ingredients
                ?.map((ingredient) => `- ${ingredient.name}: ${ingredient.quantity} ${ingredient.unit}`)
                .join("\n")}

Instructions:
${recipe.instructions?.map((instruction, index) => `${index + 1}. ${instruction}`).join("\n")}

Nutrition Information:
${Object.keys(recipe.nutrition || {})
                .map((key) => `${key}: ${recipe.nutrition[key]}`)
                .join("\n")}

Compliance Information:
${recipe.compliance || "Compliance details are unavailable."}
        `;
        navigator.clipboard.writeText(recipeText).then(() => {
            alert("Recipe copied to clipboard!");
        });
    };

    return (
        <RecipeCardContainer id="recipe-card">
            {/* Close Button */}
            <CloseButton onClick={onClose} aria-label="close">
                <CloseIcon />
            </CloseButton>

            {/* Recipe Details */}
            <Typography variant="h3" textAlign="center" style={{ color: "black", fontWeight: "bold" }}>
                {recipe.name || "Recipe Title"}
            </Typography>

            <Typography variant="h5" textAlign="center" mt={2} style={{ color: "black" }}>
                Servings: {recipe.servings || "50"} | Time: {recipe.time || "45 minutes"}
            </Typography>

            <Typography variant="h6" mt={3} style={{ color: "black" }}>
                <strong>Ingredients:</strong>
            </Typography>
            <ul>
                {recipe.ingredients?.map((ingredient, index) => (
                    <li key={index} style={{ color: "black" }}>
                        {ingredient.name} - {ingredient.quantity} {ingredient.unit}
                    </li>
                ))}
            </ul>

            <Typography variant="h6" mt={3} style={{ color: "black" }}>
                <strong>Instructions:</strong>
            </Typography>
            <ol>
                {recipe.instructions?.map((instruction, index) => (
                    <li key={index} style={{ color: "black" }}>
                        {instruction}
                    </li>
                ))}
            </ol>

            <Typography variant="h6" mt={3} style={{ color: "black" }}>
                <strong>Nutrition Information:</strong>
            </Typography>
            <Box display="flex" flexWrap="wrap" mt={2} gap={2}>
                {Object.keys(recipe.nutrition || {}).map((key, index) => (
                    <Typography key={index} style={{ color: "black" }}>
                        {key}: {recipe.nutrition[key]}
                    </Typography>
                ))}
            </Box>

            {/* Compliance Information */}
            <Typography variant="h6" mt={4} style={{ color: "black" }}>
                <strong>Compliance Information:</strong>
            </Typography>
            <Typography mt={2} style={{ color: "black" }}>
                {recipe.compliance || "Compliance details are unavailable."}
            </Typography>

            {/* Action Buttons */}
            <Box mt={4} display="flex" justifyContent="space-around">
                <Button variant="contained" onClick={handlePrint} style={{ backgroundColor: "#000", color: "#fff" }}>
                    PRINT RECIPE
                </Button>
                <Tooltip title="Copy recipe details to clipboard" arrow>
                    <Button
                        variant="contained"
                        onClick={handleCopy}
                        style={{ backgroundColor: "#1976d2", color: "#fff" }}
                    >
                        COPY RECIPE
                    </Button>
                </Tooltip>
            </Box>

            {/* Disclaimer */}
            <Typography variant="body2" mt={4} style={{ color: "gray", textAlign: "center" }}>
                Disclaimer: This recipe was generated by AI. Please verify all nutrition components and ensure compliance with USDA program requirements. Users are responsible for meeting all applicable regulations.
            </Typography>
        </RecipeCardContainer>
    );
};

export default USDARecipeCard;
